export const PET_COMMAND_EVENT = 'gyro:pet-command';
export const PET_NATIVE_STATE_EVENT = 'gyro:pet-native-state';
export const PET_HOST_AVAILABILITY_EVENT = 'gyro:pet-host-availability';
export const PET_ENROLLMENT_FEEDBACK_EVENT = 'gyro:pet-enrollment-feedback';
export const PET_PRIVACY_CLEAR_EVENT = 'gyro:pet-privacy-clear';
const activeNativeSessions = new WeakSet();
export function subscribePetHostAvailability(target, changed) {
    let previous = target.gyroPetsAvailable === true;
    if (previous)
        activeNativeSessions.add(target);
    const receive = (event) => {
        const available = event.detail;
        if (typeof available !== 'boolean' || target.gyroPetsAvailable !== available || available === previous)
            return;
        previous = available;
        if (available)
            activeNativeSessions.add(target);
        changed(available);
    };
    target.addEventListener(PET_HOST_AVAILABILITY_EVENT, receive);
    return () => target.removeEventListener(PET_HOST_AVAILABILITY_EVENT, receive);
}
export function validPetId(value) { return typeof value === 'string' && /^[A-Za-z0-9_-]{1,160}$/.test(value); }
export function parsePetContext(value) {
    if (!value || typeof value !== 'object')
        return undefined;
    const item = value;
    if (!validPetId(item.petId))
        return undefined;
    const tab = ['brief', 'overview', 'memory', 'chat', 'activity', 'results', 'forms', 'setup'].includes(String(item.tab)) ? item.tab : 'chat';
    const uuid = (id) => typeof id === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
    if (item.missionId !== undefined && !uuid(item.missionId) || item.decisionId !== undefined && (!uuid(item.decisionId) || !uuid(item.missionId) || tab !== 'overview'))
        return undefined;
    return { petId: item.petId, tab, ...(uuid(item.missionId) ? { missionId: item.missionId } : {}), ...(uuid(item.decisionId) ? { decisionId: item.decisionId } : {}) };
}
export function postPetNative(message) {
    const host = typeof window === 'undefined' ? undefined : window;
    if (host?.gyroPetsAvailable !== true || !host.chrome?.webview)
        return false;
    activeNativeSessions.add(host);
    try {
        host.chrome.webview.postMessage(message);
        return true;
    }
    catch {
        return false;
    }
}
export function openNativePet(petId, tab = 'chat', references) {
    const context = parsePetContext({ petId, tab, ...references });
    return Boolean(context && postPetNative({ type: 'pets:open', ...context }));
}
/** Propose the saved setup only; native confirmation owns enrollment dispatch. */
export function nativePetEnrollmentAvailable() {
    const host = typeof window === 'undefined' ? undefined : window;
    return host?.gyroPetsAvailable === true && host.gyroPetEnrollmentAvailable === true;
}
const enrollmentRequestId = (value) => typeof value === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/.test(value) && value.length === 36;
export function parsePetEnrollmentFeedback(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return;
    const item = value;
    if (Object.keys(item).length !== 2 || !Object.hasOwn(item, 'requestId') || !Object.hasOwn(item, 'status')
        || !enrollmentRequestId(item.requestId) || typeof item.status !== 'string'
        || !['checking', 'ready', 'busy', 'unavailable'].includes(item.status))
        return;
    return { requestId: item.requestId, status: item.status };
}
export function prepareNativePetEnrollment(petId, expectedRevision, requestId) {
    return nativePetEnrollmentAvailable() && validPetId(petId) && !/[^A-Za-z0-9_-]/.test(petId) && enrollmentRequestId(requestId)
        && Number.isSafeInteger(expectedRevision) && expectedRevision > 0 && expectedRevision <= 2147483647
        && postPetNative({ type: 'pets:enrollment-prepare', petId, expectedRevision, requestId });
}
export const PET_SOURCE_FEEDBACK_EVENT = 'gyro:pet-source-feedback';
export function parsePetSourceFeedback(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return;
    const item = value;
    if (Object.keys(item).length !== 3 || !Object.hasOwn(item, 'requestId') || !Object.hasOwn(item, 'status') || !Object.hasOwn(item, 'count')
        || !enrollmentRequestId(item.requestId) || typeof item.status !== 'string'
        || !['open', 'loading', 'ready', 'saving', 'saved', 'busy', 'unavailable', 'sign-in-required', 'closed'].includes(item.status)
        || typeof item.count !== 'number' || !Number.isInteger(item.count) || item.count < 0 || item.count > 10)
        return;
    return { requestId: item.requestId, status: item.status, count: item.count };
}
export function openNativePetSources(petId, expectedRevision, kind, requestId) {
    return nativePetEnrollmentAvailable() && validPetId(petId) && !/[^A-Za-z0-9_-]/.test(petId) && enrollmentRequestId(requestId)
        && Number.isSafeInteger(expectedRevision) && expectedRevision > 0 && expectedRevision < 2147483647
        && ['mail-folders', 'calendars'].includes(kind)
        && postPetNative({ type: 'pets:sources-open', petId, expectedRevision, kind, requestId });
}
export function cancelNativePetSources(requestId) {
    if (!enrollmentRequestId(requestId) || typeof window === 'undefined')
        return;
    // Cancellation remains deliverable when availability was withdrawn, for this exact request only.
    const host = window;
    try {
        host.chrome?.webview?.postMessage({ type: 'pets:sources-cancel', requestId });
    }
    catch { /* host already closed */ }
}
export const PET_PREVIEW_FEEDBACK_EVENT = 'gyro:pet-preview-feedback';
export function parsePetPreviewFeedback(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return;
    const item = value;
    if (Object.keys(item).length !== 2 || !Object.hasOwn(item, 'requestId') || !Object.hasOwn(item, 'status')
        || !enrollmentRequestId(item.requestId) || typeof item.status !== 'string'
        || !['open', 'working', 'ready', 'recorded', 'busy', 'unavailable', 'sign-in-required', 'closed'].includes(item.status))
        return;
    return { requestId: item.requestId, status: item.status };
}
/** Opening the native preview panel never reads sources or dispatches a provider operation. */
export function openNativePetPreview(petId, expectedRevision, kind, requestId) {
    return nativePetEnrollmentAvailable() && validPetId(petId) && !/[^A-Za-z0-9_-]/.test(petId) && enrollmentRequestId(requestId)
        && Number.isSafeInteger(expectedRevision) && expectedRevision > 0 && expectedRevision < 2147483647
        && ['mail-folders', 'calendars'].includes(kind)
        && postPetNative({ type: 'pets:preview-open', petId, expectedRevision, kind, requestId });
}
export function cancelNativePetPreview(requestId) {
    if (!enrollmentRequestId(requestId) || typeof window === 'undefined')
        return;
    const host = window;
    try {
        host.chrome?.webview?.postMessage({ type: 'pets:preview-cancel', requestId });
    }
    catch { /* exact request may already be closed */ }
}
export const PET_ACTIVATION_FEEDBACK_EVENT = 'gyro:pet-activation-feedback';
export function parsePetActivationFeedback(value) {
    if (!value || typeof value !== 'object' || Array.isArray(value))
        return;
    const item = value;
    if (Object.keys(item).length !== 2 || !Object.hasOwn(item, 'requestId') || !Object.hasOwn(item, 'status')
        || !enrollmentRequestId(item.requestId) || typeof item.status !== 'string'
        || !['open', 'working', 'ready', 'recorded', 'busy', 'unavailable', 'sign-in-required', 'closed'].includes(item.status))
        return;
    return { requestId: item.requestId, status: item.status };
}
/** Opening the native permission panel never grants access or turns a job on. */
export function openNativePetActivation(petId, expectedRevision, kind, requestId) {
    return nativePetEnrollmentAvailable() && validPetId(petId) && petId.length <= 64 && !/[^A-Za-z0-9_-]/.test(petId) && enrollmentRequestId(requestId)
        && Number.isSafeInteger(expectedRevision) && expectedRevision > 0 && expectedRevision < 2147483647
        && ['mail-folders', 'calendars', 'reply-drafts'].includes(kind)
        && postPetNative({ type: 'pets:activation-open', petId, expectedRevision, kind, requestId });
}
export function cancelNativePetActivation(requestId) {
    if (!enrollmentRequestId(requestId) || typeof window === 'undefined')
        return;
    const host = window;
    try {
        host.chrome?.webview?.postMessage({ type: 'pets:activation-cancel', requestId });
    }
    catch { /* exact request may already be closed */ }
}
export function clearNativePets() {
    // Invalidate an active or previously active session, including after availability
    // was withdrawn. A window that never had companions emits no companion messages.
    const host = typeof window === 'undefined' ? undefined : window;
    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function')
        window.dispatchEvent(new Event(PET_PRIVACY_CLEAR_EVENT));
    if (!host || host.gyroPetsAvailable !== true && !activeNativeSessions.has(host))
        return;
    try {
        host.chrome?.webview?.postMessage({ type: 'pets:privacy-clear' });
        activeNativeSessions.delete(host);
    }
    catch { /* the native window may already be gone */ }
}
export function syncNativePets() { postPetNative({ type: 'pets:sync' }); }
//# sourceMappingURL=native.js.map